/**
 * Room-boundary helpers — shared between the Pinia store (which clamps
 * `moveRoom` requests) and `RoomSection` (which disables the up/down arrows).
 *
 * The Pool room is a fixed-shape sign-up room that cannot move; it also acts
 * as a wall so no other room can be reordered into its slot. Both call sites
 * previously re-derived the same `poolIndex - 1` boundary; this module is
 * the single source of truth for the boundary math.
 */
import type { Room } from '../types'

/**
 * Highest legal `targetIndex` for `moveRoom`. When the Pool exists it's the
 * index immediately above it (`poolIndex - 1`); otherwise it's the last
 * room in the array. Returns `-1` only when the input list is empty or the
 * Pool is the sole room (nothing is movable).
 */
export function lastMovableRoomIndex(rooms: ReadonlyArray<Room>): number {
  const poolIndex = rooms.findIndex((r) => r.id === 'pool')
  if (poolIndex >= 0) return poolIndex - 1
  return rooms.length - 1
}

/**
 * True when `roomIndex` is at (or past) the move boundary — i.e. there is
 * no room to swap down into. The negative result short-circuits when the
 * room itself isn't found in the list.
 */
export function isAtLastMovablePosition(
  rooms: ReadonlyArray<Room>,
  roomIndex: number,
): boolean {
  if (roomIndex < 0) return false
  const last = lastMovableRoomIndex(rooms)
  if (last < 0) return true
  return roomIndex >= last
  }